import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, Calendar as CalendarIcon, DoorClosed, MapPin, HelpCircle, CreditCard, Wrench, Sparkles, Search, Check } from "lucide-react";
import RoomWizard from "./RoomWizard";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

export default function BranchSettings() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const [branch, setBranch] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [surchargeTypes, setSurchargeTypes] = useState([]);
  const [equipment, setEquipment] = useState([]);

  // Per-item CRUD permissions. Each Branch Settings sub-tab reads only
  // its own map: View toggle governs tab visibility, Create/Edit/Delete
  // toggles govern the individual buttons and icons inside the panel.
  // When View is off for a tab, the whole tab is stripped from the list
  // (never rendered), so a viewer without View cannot even attempt to
  // reach the panel by tab-clicking. If every child View is off (or the
  // parent gate is off) we redirect the user back to the branches list.
  const perms = useMemo(() => ({
    parent: hasPermission(staff, "branch_settings.view"),
    rooms: {
      view: hasPermission(staff, "branch_settings.rooms.view"),
      create: hasPermission(staff, "branch_settings.rooms.create"),
      edit: hasPermission(staff, "branch_settings.rooms.edit"),
      delete: hasPermission(staff, "branch_settings.rooms.delete"),
    },
    appointments: {
      view: hasPermission(staff, "branch_settings.appointments.view"),
      edit: hasPermission(staff, "branch_settings.appointments.edit"),
    },
    services: {
      view: hasPermission(staff, "branch_settings.services.view"),
      edit: hasPermission(staff, "branch_settings.services.edit"),
    },
    payment_types: {
      view: hasPermission(staff, "branch_settings.payment_types.view"),
      create: hasPermission(staff, "branch_settings.payment_types.create"),
      edit: hasPermission(staff, "branch_settings.payment_types.edit"),
      delete: hasPermission(staff, "branch_settings.payment_types.delete"),
    },
    equipment: {
      view: hasPermission(staff, "branch_settings.equipment.view"),
      create: hasPermission(staff, "branch_settings.equipment.create"),
      edit: hasPermission(staff, "branch_settings.equipment.edit"),
      delete: hasPermission(staff, "branch_settings.equipment.delete"),
    },
    location_info: {
      view: hasPermission(staff, "branch_settings.location_info.view"),
      edit: hasPermission(staff, "branch_settings.location_info.edit"),
    },
  }), [staff]);

  const tabs = useMemo(() => (
    [
      { key: "rooms",         label: "Rooms",         icon: DoorClosed,   show: perms.rooms.view },
      { key: "appointments",  label: "Appointments",  icon: CalendarIcon, show: perms.appointments.view },
      { key: "services",      label: "Services",      icon: Sparkles,     show: perms.services.view },
      { key: "payment-types", label: "Payment Types", icon: CreditCard,   show: perms.payment_types.view },
      { key: "equipment",     label: "Equipment",     icon: Wrench,       show: perms.equipment.view },
      { key: "info",          label: "Location Info", icon: MapPin,       show: perms.location_info.view },
    ].filter((t) => t.show)
  ), [perms]);

  const load = useCallback(async () => {
    try {
      const [b, s, r, pt, st, eq] = await Promise.all([
        staffApi.get("/branches"),
        staffApi.get(`/branches/${bid}/settings`),
        staffApi.get(`/branches/${bid}/rooms`),
        staffApi.get(`/branches/${bid}/payment-types`),
        staffApi.get(`/branches/${bid}/surcharge-types`),
        staffApi.get(`/branches/${bid}/equipment`),
      ]);
      setBranch(b.data.find((x) => x.id === bid) || null);
      setSettings(s.data);
      setRooms(r.data);
      setPaymentTypes(pt.data);
      setSurchargeTypes(st.data);
      setEquipment(eq.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [bid]);

  useEffect(() => { load(); }, [load]);

  // Kick the user out entirely when the parent gate is off or every child
  // is denied — matches the promise that "View OFF hides the menu item".
  if (!perms.parent || tabs.length === 0) {
    return <Navigate to={`/staff/branch/${bid}/calendar`} replace />;
  }

  const firstTab = tabs[0].key;

  return (
    <StaffShell context="branch" branchName={branch?.name}>
      <div className="max-w-5xl">
        <p className="tracking-overline text-xs text-muted-foreground">LOCATION SETTINGS</p>
        <h1 className="text-3xl font-semibold mt-1 mb-1" data-testid="settings-title">{branch?.name || "Location"} · Settings</h1>
        <p className="text-sm text-muted-foreground mb-6">Manage rooms, appointment defaults, services, payment options and location information for this branch. Staff profiles now live under <b>Brand → Staff Logins</b>.</p>

        <Tabs defaultValue={firstTab} className="w-full">
          <TabsList data-testid="settings-tabs">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="gap-2">
                  <Icon className="w-4 h-4" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {perms.rooms.view && (
            <TabsContent value="rooms" className="mt-4">
              <RoomsPanel bid={bid} rooms={rooms} reload={load} perms={perms.rooms} />
            </TabsContent>
          )}
          {perms.appointments.view && (
            <TabsContent value="appointments" className="mt-4">
              {settings && <AppointmentsPanel bid={bid} settings={settings} reload={load} perms={perms.appointments} />}
            </TabsContent>
          )}
          {perms.services.view && (
            <TabsContent value="services" className="mt-4">
              <ServicesPanel bid={bid} onSaved={load} perms={perms.services} />
            </TabsContent>
          )}
          {perms.payment_types.view && (
            <TabsContent value="payment-types" className="mt-4 space-y-4">
              <PriceModePanel bid={bid} settings={settings} reload={load} perms={perms.payment_types} />
              <PaymentTypesPanel bid={bid} items={paymentTypes} reload={load} perms={perms.payment_types} />
              <SurchargeTypesPanel bid={bid} items={surchargeTypes} reload={load} perms={perms.payment_types} />
            </TabsContent>
          )}
          {perms.equipment.view && (
            <TabsContent value="equipment" className="mt-4">
              <EquipmentPanel bid={bid} items={equipment} reload={load} perms={perms.equipment} />
            </TabsContent>
          )}
          {perms.location_info.view && (
            <TabsContent value="info" className="mt-4">
              {settings && <LocationInfoPanel bid={bid} settings={settings} reload={load} perms={perms.location_info} />}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </StaffShell>
  );
}

/* ---------------- Rooms ---------------- */
function RoomsPanel({ bid, rooms, reload, perms }) {
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const filtered = rooms.filter((r) => (r.name || "").toLowerCase().includes(search.trim().toLowerCase()));

  const openNew = () => { setEditing(null); setWizardOpen(true); };
  const openEdit = (r) => { setEditing(r); setWizardOpen(true); };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    try { await staffApi.delete(`/rooms/${r.id}`); reload(); toast.success("Room removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-sm text-muted-foreground">Treatment rooms available at this location. Room selection runs in the background for online bookings — customers are never asked which room.</p>
          </div>
          {perms.create && (
            <Button onClick={openNew} className="gap-1" data-testid="rooms-new-btn"><Plus className="w-4 h-4" /> New Room</Button>
          )}
        </div>

        <div className="mb-4 max-w-sm">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rooms…"
              className="pl-9"
              data-testid="rooms-search"
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-2" data-testid="rooms-results-count">Results {filtered.length} of {rooms.length}</p>

        <div className="rounded-lg border border-border overflow-hidden">
          <div className="grid grid-cols-[2fr_100px_3fr_120px] gap-3 px-4 py-2 bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <span>Name</span>
            <span>Capacity</span>
            <span>Description</span>
            <span className="text-right">Actions</span>
          </div>
          {filtered.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground text-center">No rooms yet.</p>
          )}
          {filtered.map((r) => {
            const status = r.status || "working";
            const cap = Number(r.capacity ?? 1);
            const busy = Number(r.busy_now ?? 0);
            const net = Number(r.net_available ?? (status === "working" && r.active !== false ? cap : 0));
            const badgeClass = status === "working"
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : status === "maintenance"
                ? "bg-amber-100 text-amber-800 border-amber-200"
                : "bg-rose-100 text-rose-800 border-rose-200";
            const statusLabel = status === "working" ? "Working / Clean" : status === "maintenance" ? "Maintenance" : "Out of Service";
            return (
              <div
                key={r.id}
                className="grid grid-cols-[2fr_100px_3fr_120px] gap-3 px-4 py-2.5 items-center border-t border-border hover:bg-secondary/20"
                data-testid={`room-row-${r.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate flex items-center gap-2 flex-wrap">
                    <span>{r.name}</span>
                    <span
                      className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${badgeClass}`}
                      data-testid={`room-status-badge-${r.id}`}
                    >{statusLabel}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid={`room-busy-${r.id}`}>
                    Busy now: <span className="font-medium text-foreground">{busy}</span> / {net} available
                    {cap > net && <span className="ml-2 text-rose-700">({cap - net} offline)</span>}
                  </p>
                </div>
                <span>{cap}</span>
                <span className="text-sm text-muted-foreground truncate" title={r.description || ""}>{r.description || ""}</span>
                <div className="flex items-center gap-3 justify-end text-sm">
                  {perms.edit && (
                    <button className="text-primary hover:underline" onClick={() => openEdit(r)} data-testid={`room-edit-${r.id}`}>Edit</button>
                  )}
                  {perms.delete && (
                    <button className="text-destructive hover:underline" onClick={() => remove(r)} data-testid={`room-delete-${r.id}`}>Delete</button>
                  )}
                  {!perms.edit && !perms.delete && (
                    <span className="text-muted-foreground italic text-xs">Read-only</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      <RoomWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        bid={bid}
        room={editing}
        onSaved={reload}
      />
    </Card>
  );
}

/* ---------------- Appointment settings ---------------- */
function HelpHint({ text, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full w-5 h-5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          data-testid={testid}
          aria-label="More information"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="max-w-xs text-xs leading-relaxed">
        {text}
      </PopoverContent>
    </Popover>
  );
}

function YesNoRow({ label, description, checked, onChange, testid, helpTestid, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-t border-border first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">{label}</Label>
          {description && <HelpHint text={description} testid={helpTestid} />}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs ${checked ? "text-muted-foreground" : "font-medium"}`}>No</span>
        <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
        <span className={`text-xs ${checked ? "font-medium" : "text-muted-foreground"}`}>Yes</span>
      </div>
    </div>
  );
}

function AppointmentsPanel({ bid, settings, reload, perms }) {
  const [a, setA] = useState(settings.appointment_settings || {});
  useEffect(() => { setA(settings.appointment_settings || {}); }, [settings]);

  const save = async () => {
    try {
      await staffApi.put(`/branches/${bid}/settings`, { appointment_settings: a });
      toast.success("Appointment settings saved"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const upd = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const readOnly = !perms.edit;

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6 grid gap-6 max-w-2xl">
        <p className="text-sm text-muted-foreground">
          Defaults for online bookings and calendar behavior.
          {readOnly && <span className="ml-2 italic">· Read-only</span>}
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Cancellation window (hours)</Label><Input type="number" value={a.cancellation_hours ?? 24} onChange={(e) => upd("cancellation_hours", parseInt(e.target.value || 0))} disabled={readOnly} data-testid="ap-cancel-hours" /></div>
          <div><Label>Booking window (days ahead)</Label><Input type="number" value={a.booking_window_days ?? 60} onChange={(e) => upd("booking_window_days", parseInt(e.target.value || 0))} disabled={readOnly} data-testid="ap-booking-window" /></div>
          <div className="flex items-end gap-3"><Switch checked={a.allow_online_booking !== false} onCheckedChange={(v) => upd("allow_online_booking", v)} disabled={readOnly} data-testid="ap-online-toggle" /><Label>Allow online booking</Label></div>
        </div>

        <div className="rounded-lg border border-border px-4">
          <YesNoRow
            label="Allow appointment in the past"
            description={"Select \u201cYes\u201d to allow users to book appointments in the past. This setting is useful for entering walk-in appointments after they happen if your front desk was unable to enter the appointment in advance."}
            checked={a.allow_past_appointments === true}
            onChange={(v) => upd("allow_past_appointments", v)}
            disabled={readOnly}
            testid="ap-past-toggle"
            helpTestid="ap-past-help"
          />
          <YesNoRow
            label="Enable No Show Alert"
            description={"When enabled, the system automatically pops up an alert as soon as staff types a customer name whose record shows more than two no-shows within the past year."}
            checked={a.enable_no_show_alert === true}
            onChange={(v) => upd("enable_no_show_alert", v)}
            disabled={readOnly}
            testid="ap-noshow-toggle"
            helpTestid="ap-noshow-help"
          />
        </div>

        {perms.edit && (
          <div><Button onClick={save} data-testid="save-appointment-settings">Save changes</Button></div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------- Location info ---------------- */
const DAY_SHORT = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

function normalizeClosed(list) {
  return (list || []).map((c) => (typeof c === "string" ? { date: c, description: "" } : { date: c.date, description: c.description || "" }));
}

function formatDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
function weekdayName(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
}

function LocationInfoPanel({ bid, settings, reload, perms }) {
  const [f, setF] = useState(() => initialForm(settings));
  useEffect(() => { setF(initialForm(settings)); }, [settings]);

  const [newClosed, setNewClosed] = useState({ date: "", description: "" });
  const readOnly = !perms.edit;

  const patch = (key, value) => setF((s) => ({ ...s, [key]: value }));
  const setHours = (day, k, v) =>
    setF((s) => ({ ...s, opening_hours: { ...s.opening_hours, [day]: { ...s.opening_hours[day], [k]: v } } }));

  const addClosed = () => {
    if (!newClosed.date) return;
    setF((s) => {
      const existing = normalizeClosed(s.closed_dates).filter((c) => c.date !== newClosed.date);
      const next = [...existing, { date: newClosed.date, description: newClosed.description.trim() }].sort((a, b) => a.date.localeCompare(b.date));
      return { ...s, closed_dates: next };
    });
    setNewClosed({ date: "", description: "" });
  };
  const removeClosed = (dateIso) =>
    setF((s) => ({ ...s, closed_dates: normalizeClosed(s.closed_dates).filter((c) => c.date !== dateIso) }));

  const save = async () => {
    try {
      const payload = { ...f, closed_dates: normalizeClosed(f.closed_dates) };
      await staffApi.put(`/branches/${bid}/settings`, payload);
      toast.success("Location info saved"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closed = normalizeClosed(f.closed_dates);

  return (
    <div className="space-y-4">
      {/* Top row: Address · Primary Contact · Secondary Contact */}
      <div className="grid gap-4 lg:grid-cols-3">
        <InfoCard title="Address" testid="info-card-address">
          <Textarea rows={4} value={f.address} onChange={(e) => patch("address", e.target.value)} placeholder="Street, city, postal code" disabled={readOnly} data-testid="info-address" className="text-sm" />
        </InfoCard>

        <InfoCard title="Primary Contact" testid="info-card-primary">
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={f.contact_name} onChange={(e) => patch("contact_name", e.target.value)} placeholder="Cocoon Medical Spa" disabled={readOnly} data-testid="info-contact-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input value={f.email} onChange={(e) => patch("email", e.target.value)} placeholder="info@cocoonmedicalspa.com" disabled={readOnly} data-testid="info-email" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <Input value={f.phone} onChange={(e) => patch("phone", e.target.value)} placeholder="+62 811 388 815" disabled={readOnly} data-testid="info-phone" />
            </div>
          </div>
        </InfoCard>

        <InfoCard title="Secondary Contact" testid="info-card-secondary">
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={f.secondary_name} onChange={(e) => patch("secondary_name", e.target.value)} placeholder="Optional" disabled={readOnly} data-testid="info-secondary-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">WhatsApp</Label>
              <Input value={f.whatsapp} onChange={(e) => patch("whatsapp", e.target.value)} placeholder="+62 …" disabled={readOnly} data-testid="info-whatsapp" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Input value={f.secondary_notes} onChange={(e) => patch("secondary_notes", e.target.value)} placeholder="Alternate contact / website" disabled={readOnly} data-testid="info-secondary-notes" />
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Bottom row: Hours + Closed Dates */}
      <div className="grid gap-4 lg:grid-cols-2">
        <InfoCard title="Hours of Operation" testid="info-card-hours">
          <div className="rounded-md border border-border overflow-hidden">
            <div className="grid grid-cols-[1.4fr_auto_1fr_1fr] gap-2 items-center bg-secondary/40 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span>Day</span>
              <span className="text-center">Open</span>
              <span>Open time</span>
              <span>Close time</span>
            </div>
            {DAYS.map((d) => {
              const h = f.opening_hours?.[d.key] || { open: true, open_time: "09:00", close_time: "21:00" };
              return (
                <div key={d.key} className="grid grid-cols-[1.4fr_auto_1fr_1fr] gap-2 items-center px-3 py-1.5 border-t border-border">
                  <div className="text-sm font-medium">{d.label}</div>
                  <Switch checked={h.open !== false} onCheckedChange={(v) => setHours(d.key, "open", v)} disabled={readOnly} data-testid={`hours-${d.key}-toggle`} />
                  <Input type="time" value={h.open_time || "09:00"} onChange={(e) => setHours(d.key, "open_time", e.target.value)} disabled={readOnly || h.open === false} data-testid={`hours-${d.key}-open`} className="h-8 text-sm" />
                  <Input type="time" value={h.close_time || "21:00"} onChange={(e) => setHours(d.key, "close_time", e.target.value)} disabled={readOnly || h.open === false} data-testid={`hours-${d.key}-close`} className="h-8 text-sm" />
                </div>
              );
            })}
          </div>
        </InfoCard>

        <InfoCard title="Closed Dates" testid="info-card-closed">
          <div className="rounded-md border border-border overflow-hidden mb-3 max-h-[280px] overflow-y-auto">
            {closed.length === 0 && (
              <p className="p-6 text-sm text-muted-foreground text-center">No closed dates yet.</p>
            )}
            {closed.map((c, idx) => (
              <div
                key={c.date}
                className={`grid grid-cols-[130px_1fr_auto] gap-3 items-center px-3 py-2 text-sm ${idx % 2 === 0 ? "bg-background" : "bg-secondary/30"}`}
                data-testid={`closed-date-${c.date}`}
              >
                <span className="font-medium whitespace-nowrap">{formatDate(c.date)}</span>
                <span className="text-muted-foreground truncate">{c.description || weekdayName(c.date)}</span>
                {perms.edit && (
                  <button
                    type="button"
                    onClick={() => removeClosed(c.date)}
                    aria-label={`Remove ${c.date}`}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`closed-date-remove-${c.date}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {perms.edit && (
            <div className="grid grid-cols-[140px_1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input type="date" value={newClosed.date} onChange={(e) => setNewClosed((s) => ({ ...s, date: e.target.value }))} data-testid="new-closed-date" className="h-9" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Input value={newClosed.description} onChange={(e) => setNewClosed((s) => ({ ...s, description: e.target.value }))} placeholder="e.g. Nyepi, Christmas Day" data-testid="new-closed-description" className="h-9" />
              </div>
              <Button variant="outline" onClick={addClosed} data-testid="add-closed-date-btn" className="h-9">Add</Button>
            </div>
          )}
        </InfoCard>
      </div>

      {perms.edit && (
        <div className="flex justify-end">
          <Button onClick={save} data-testid="save-location-info">Save location info</Button>
        </div>
      )}
    </div>
  );
}

function InfoCard({ title, children, testid }) {
  return (
    <Card className="rounded-xl overflow-hidden" data-testid={testid}>
      <div className="bg-secondary/40 px-4 py-2 text-sm font-semibold border-b border-border">{title}</div>
      <CardContent className="p-4">{children}</CardContent>
    </Card>
  );
}

function initialForm(settings) {
  return {
    address: settings.address || "",
    phone: settings.phone || "",
    whatsapp: settings.whatsapp || "",
    email: settings.email || "",
    contact_name: settings.contact_name || "",
    secondary_name: settings.secondary_name || "",
    secondary_notes: settings.secondary_notes || "",
    opening_hours: settings.opening_hours || {},
    closed_dates: normalizeClosed(settings.closed_dates),
  };
}


/* ---------------- Payment Types ---------------- */
// Category options that map each payment type to a Checkout page tab.
// Kept in sync with backend `models.py` PAYMENT_CATEGORIES.
const PAYMENT_CATEGORIES = [
  { value: "payment_on_file",  label: "Payment On File" },
  { value: "credit_card",      label: "Credit / Debit Card" },
  { value: "online",           label: "Online" },
  { value: "marketing",        label: "Marketing" },
  { value: "cash_equivalent",  label: "Cash / Equivalent" },
  { value: "account",          label: "Account" },
  { value: "other",            label: "Other / More" },
];
const CATEGORY_LABEL = Object.fromEntries(PAYMENT_CATEGORIES.map((c) => [c.value, c.label]));

function PaymentTypesPanel({ bid, items, reload, perms }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("cash_equivalent");

  const openNew = () => { setEditing(null); setName(""); setCategory("cash_equivalent"); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setName(p.name); setCategory(p.category || "other"); setOpen(true); };

  const save = async () => {
    const n = name.trim();
    if (!n) return toast.error("Name is required");
    try {
      if (editing) {
        await staffApi.put(`/payment-types/${editing.id}`, { name: n, category });
        toast.success("Payment type updated");
      } else {
        await staffApi.post(`/branches/${bid}/payment-types`, { name: n, category, active: true });
        toast.success("Payment type added");
      }
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (p) => {
    try { await staffApi.put(`/payment-types/${p.id}`, { active: !p.active }); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await staffApi.delete(`/payment-types/${p.id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">Payment methods your team can use when closing a bill at this location. The <span className="font-medium">Category</span> decides which Checkout tab they appear under.</p>
          {perms.create && (
            <Button size="sm" onClick={openNew} data-testid="add-payment-type-btn" className="gap-1"><Plus className="w-4 h-4" /> New payment type</Button>
          )}
        </div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No payment types yet.</p>}
          {items.map((p) => (
            <div key={p.id} className="p-3.5 flex items-center justify-between gap-3" data-testid={`payment-type-row-${p.id}`}>
              <div className="min-w-0 flex items-center gap-3">
                <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                <p className="font-medium truncate">{p.name}</p>
                <Badge variant="outline" className="rounded-full text-[10px] font-normal" data-testid={`payment-type-category-${p.id}`}>{CATEGORY_LABEL[p.category] || "Other / More"}</Badge>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {perms.edit && (
                  <Switch checked={p.active !== false} onCheckedChange={() => toggleActive(p)} data-testid={`payment-type-toggle-${p.id}`} />
                )}
                {perms.edit && (
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-payment-type-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                )}
                {perms.delete && (
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`delete-payment-type-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
                {!perms.edit && !perms.delete && (
                  <span className="text-muted-foreground italic text-xs">Read-only</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payment-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit payment type" : "New payment type"}</DialogTitle>
            <DialogDescription>Payment methods available when closing a bill at this location.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QRIS" autoFocus data-testid="payment-type-name-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="payment-type-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Decides which Checkout tab this payment type appears under.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-payment-type-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Prices Are (per branch) ---------------- */
// Location-level toggle that governs how service prices compose with the branch's
// surcharge_types at checkout:
//   • "nett"     → the price on the service already contains all surcharges + tax;
//                  invoices reverse-decompose it back into base + surcharge + tax so
//                  the customer sees the breakdown.
//   • "plus_tax" → the price is exclusive; the invoice adds each surcharge and each
//                  tax on top and lists them at the bottom.
// This used to live on each service (`pricing.price_mode`); moved here per operator
// request so a single branch decision drives every service consistently.
function PriceModePanel({ bid, settings, reload, perms }) {
  const current = settings?.price_mode || "plus_tax";
  const [busy, setBusy] = useState(false);
  const readOnly = !perms.edit;

  const setMode = async (v) => {
    if (v === current) return;
    setBusy(true);
    try {
      await staffApi.put(`/branches/${bid}/settings`, { price_mode: v });
      toast.success("Price mode updated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-medium">Prices are</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Controls how service prices at this location compose with the surcharge and tax rules configured above.
            </p>
          </div>
          <div className="w-full sm:w-72">
            <Select value={current} onValueChange={setMode} disabled={busy || readOnly}>
              <SelectTrigger data-testid="branch-price-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nett">Nett (surcharge &amp; tax already included)</SelectItem>
                <SelectItem value="plus_tax">Add. Surcharge and Tax (added on top at checkout)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-2" data-testid="branch-price-mode-hint">
              {current === "nett"
                ? "Invoice will show a breakdown that decomposes the nett price back into base + surcharge + tax."
                : "Invoice will add each surcharge / tax on top of the price and list them at the bottom."}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Surcharge Types ---------------- */
// Location-level list of additional charges/taxes.
// - name   : label shown at checkout (e.g. "Service charge", "PPN 11%").
// - rate   : percentage (0..100), never negative.
// - allow_flexible: when true, cashiers may override the rate on a specific order.
// - category:
//     • "surcharge" → applied on the service price   → price + surcharge
//     • "tax"       → applied on the pre-tax total   → (price + surcharge) + tax
// - active : soft on/off toggle without deleting the record.
function SurchargeTypesPanel({ bid, items, reload, perms }) {
  const blank = { name: "", rate: 0, allow_flexible: false, category: "surcharge", active: true };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      rate: Number(s.rate) || 0,
      allow_flexible: !!s.allow_flexible,
      category: s.category || "surcharge",
      active: s.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    if (form.rate < 0) return toast.error("Rate cannot be negative");
    const payload = { ...form, name, rate: Number(form.rate) || 0 };
    try {
      if (editing) {
        await staffApi.put(`/surcharge-types/${editing.id}`, payload);
        toast.success("Surcharge type updated");
      } else {
        await staffApi.post(`/branches/${bid}/surcharge-types`, payload);
        toast.success("Surcharge type added");
      }
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (s) => {
    try { await staffApi.put(`/surcharge-types/${s.id}`, { active: !s.active }); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await staffApi.delete(`/surcharge-types/${s.id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium">Surcharge Types</p>
            <p className="text-xs text-muted-foreground mt-0.5">Service charges and taxes applied on top of the service price. Surcharges apply first, then taxes apply on the pre-tax total.</p>
          </div>
          {perms.create && (
            <Button size="sm" onClick={openNew} data-testid="add-surcharge-type-btn" className="gap-1"><Plus className="w-4 h-4" /> New surcharge type</Button>
          )}
        </div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center" data-testid="surcharge-types-empty">No surcharge types yet.</p>}
          {items.map((s) => (
            <div key={s.id} className="p-3.5 flex items-center justify-between gap-3" data-testid={`surcharge-type-row-${s.id}`}>
              <div className="min-w-0 flex items-center gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate flex items-center gap-2">
                    {s.name}
                    <span className="inline-flex items-center rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{s.category}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{Number(s.rate).toLocaleString("id-ID")}% {s.allow_flexible ? "· flexible" : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {perms.edit && (
                  <Switch checked={s.active !== false} onCheckedChange={() => toggleActive(s)} data-testid={`surcharge-type-toggle-${s.id}`} />
                )}
                {perms.edit && (
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-surcharge-type-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                )}
                {perms.delete && (
                  <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`delete-surcharge-type-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
                {!perms.edit && !perms.delete && (
                  <span className="text-muted-foreground italic text-xs">Read-only</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="surcharge-type-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit surcharge type" : "New surcharge type"}</DialogTitle>
            <DialogDescription>Additional service charges or taxes applied at checkout.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Service Charge, PPN"
                autoFocus
                data-testid="surcharge-type-name-input"
              />
            </div>

            <div>
              <Label>Rate</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  className="flex-1"
                  data-testid="surcharge-type-rate-input"
                />
                <span className="text-sm text-muted-foreground w-6 text-center">%</span>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="mb-0">Allow flexible</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="surcharge-type-flexible-help">
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="right" className="max-w-xs text-xs leading-relaxed" data-testid="surcharge-type-flexible-help-content">
                    If this is set to Yes, the tax rate may be overridden for specific orders when necessary.
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!form.allow_flexible}
                  onCheckedChange={(v) => setForm({ ...form, allow_flexible: v })}
                  data-testid="surcharge-type-flexible-switch"
                />
                <span className="text-xs text-muted-foreground">{form.allow_flexible ? "Yes" : "No"}</span>
              </div>
            </div>

            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="surcharge-type-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="surcharge">Surcharge (applied on service price)</SelectItem>
                  <SelectItem value="tax">Tax (applied on price + surcharge)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Surcharge: total = price + surcharge. Tax: total = (price + surcharge) + tax.
              </p>
            </div>

            <div>
              <Label>Active</Label>
              <div className="flex items-center gap-2 mt-1">
                <Switch
                  checked={!!form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="surcharge-type-active-switch"
                />
                <span className="text-xs text-muted-foreground">{form.active ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-surcharge-type-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Equipment ---------------- */
const STATUS_LABEL = {
  working: "Working",
  out_of_service: "Out of Service",
  out_of_order: "Out of Order",
};
const STATUS_BADGE = {
  working: "bg-emerald-100 text-emerald-800 border-emerald-200",
  out_of_service: "bg-amber-100 text-amber-800 border-amber-200",
  out_of_order: "bg-rose-100 text-rose-800 border-rose-200",
};

function EquipmentPanel({ bid, items, reload, perms }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", notes: "", quantity: 1, status: "working" });

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", notes: "", quantity: 1, status: "working" }); setOpen(true); };
  const openEdit = (eq) => {
    setEditing(eq);
    setForm({
      name: eq.name,
      code: eq.code || "",
      notes: eq.notes || "",
      quantity: Number(eq.quantity ?? 1),
      status: eq.status || "working",
    });
    setOpen(true);
  };

  const showOverbookAlert = (name, conflicts) => {
    if (!Array.isArray(conflicts) || conflicts.length === 0) return;
    // Consolidate into one urgent toast so the operator sees the
    // full damage rather than a rapid-fire of one-per-day toasts.
    const summary = conflicts
      .slice(0, 3)
      .map((c) => `${c.date} · ${c.over_by} to reschedule`)
      .join("\n");
    const extra = conflicts.length > 3 ? `\n… + ${conflicts.length - 3} more day(s)` : "";
    toast.error(
      `Conflict Detected: Branch is over-booked for ${name}.\n${summary}${extra}`,
      { duration: 10000, id: `equip-conflict-${name}` },
    );
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!Number.isFinite(Number(form.quantity)) || Number(form.quantity) < 1) return toast.error("Quantity must be at least 1");
    try {
      let res;
      if (editing) {
        res = await staffApi.put(`/equipment/${editing.id}`, {
          name: form.name,
          code: form.code,
          notes: form.notes,
          quantity: Number(form.quantity),
          status: form.status,
        });
        toast.success("Equipment updated");
      } else {
        res = await staffApi.post(`/branches/${bid}/equipment`, {
          name: form.name,
          code: form.code,
          notes: form.notes,
          quantity: Number(form.quantity),
          status: form.status,
          active: true,
        });
        toast.success("Equipment added");
      }
      showOverbookAlert(form.name, res?.data?.conflicts);
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (eq) => {
    try { await staffApi.put(`/equipment/${eq.id}`, { active: !eq.active }); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (eq) => {
    if (!window.confirm(`Delete "${eq.name}"?`)) return;
    try { await staffApi.delete(`/equipment/${eq.id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">Spa or clinic equipment and machines available at this location.</p>
          {perms.create && (
            <Button size="sm" onClick={openNew} data-testid="add-equipment-btn" className="gap-1"><Plus className="w-4 h-4" /> New equipment</Button>
          )}
        </div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No equipment yet.</p>}
          {items.map((eq) => {
            const status = eq.status || "working";
            const qty = Number(eq.quantity ?? 1);
            const busy = Number(eq.busy_now ?? 0);
            const net = Number(eq.net_available ?? (status === "working" ? qty : 0));
            return (
              <div key={eq.id} className="p-3.5 flex items-center justify-between gap-3" data-testid={`equipment-row-${eq.id}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <Wrench className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate flex items-center gap-2 flex-wrap">
                      <span>{eq.name}</span>
                      {eq.code && <span className="text-xs text-muted-foreground">({eq.code})</span>}
                      <span
                        className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${STATUS_BADGE[status]}`}
                        data-testid={`equipment-status-badge-${eq.id}`}
                      >
                        {STATUS_LABEL[status]}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`equipment-busy-${eq.id}`}>
                      Busy now: <span className="font-medium text-foreground">{busy}</span> / {net} available
                      {qty > net && <span className="ml-2 text-rose-700">({qty - net} offline)</span>}
                    </p>
                    {eq.notes && <p className="text-xs text-muted-foreground truncate mt-0.5">{eq.notes}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {perms.edit && (
                    <Switch checked={eq.active !== false} onCheckedChange={() => toggleActive(eq)} data-testid={`equipment-toggle-${eq.id}`} />
                  )}
                  {perms.edit && (
                    <Button size="sm" variant="ghost" onClick={() => openEdit(eq)} data-testid={`edit-equipment-${eq.id}`}><Pencil className="w-4 h-4" /></Button>
                  )}
                  {perms.delete && (
                    <Button size="sm" variant="ghost" onClick={() => remove(eq)} data-testid={`delete-equipment-${eq.id}`}><Trash2 className="w-4 h-4" /></Button>
                  )}
                  {!perms.edit && !perms.delete && (
                    <span className="text-muted-foreground italic text-xs">Read-only</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="equipment-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit equipment" : "New equipment"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fotona SP Dynamis Laser" autoFocus data-testid="equipment-name-input" />
            </div>
            <div>
              <Label>Serial / asset code (optional)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. FOT-2024-01" data-testid="equipment-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity (Total)</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  data-testid="equipment-quantity-input"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="equipment-status-select"
                >
                  <option value="working">Working</option>
                  <option value="out_of_service">Out of Service</option>
                  <option value="out_of_order">Out of Order</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Maintenance date, brand, room…" data-testid="equipment-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-equipment-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Per-branch service configuration ---------------- */
function MultiSelectChips({ label, options, selected, onChange, placeholder, testid, empty, disabled }) {
  const [q, setQ] = useState("");
  const selectedSet = new Set(selected);
  const filtered = options.filter((o) =>
    !q.trim() ? true : (o.label || "").toLowerCase().includes(q.trim().toLowerCase()),
  );
  const toggle = (id) => {
    if (disabled) return;
    const next = selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={`multi-${testid}`}>
      <Label className="text-sm">{label}</Label>
      {options.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{empty || `No ${label.toLowerCase()} configured for this location yet.`}</p>
      ) : (
        <>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder || `Search ${label.toLowerCase()}…`}
              className="pl-8 h-9"
              data-testid={`multi-${testid}-search`}
            />
          </div>
          <div className="max-h-52 overflow-auto rounded-md border border-border divide-y divide-border bg-white">
            {filtered.map((o) => {
              const on = selectedSet.has(o.id);
              return (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => toggle(o.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-secondary ${on ? "bg-primary/5" : ""}`}
                  data-testid={`multi-${testid}-opt-${o.id}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                  {o.hint && <span className="ml-auto text-xs text-muted-foreground">{o.hint}</span>}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="p-3 text-xs text-muted-foreground text-center">No matches.</p>}
          </div>
          {selected.length > 0 && (
            <p className="text-[11px] text-muted-foreground">{selected.length} selected</p>
          )}
        </>
      )}
    </div>
  );
}

function ServicesPanel({ bid, onSaved, perms }) {
  const [services, setServices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [staffOpts, setStaffOpts] = useState([]);
  const [roomOpts, setRoomOpts] = useState([]);
  const [equipmentOpts, setEquipmentOpts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const readOnly = !perms.edit;

  const load = useCallback(async () => {
    try {
      // Product consumables are intentionally NOT loaded here — the operator
      // manages them at the brand catalog level; the Services tab focuses on
      // who / where / with-what delivers the service at this location.
      const [svcs, staff, rooms, eq] = await Promise.all([
        staffApi.get(`/branches/${bid}/service-configs`),
        staffApi.get(`/branches/${bid}/employees`),
        staffApi.get(`/branches/${bid}/rooms`),
        staffApi.get(`/branches/${bid}/equipment`),
      ]);
      setServices(svcs.data);
      // STRICT CARE-TEAM + ACTIVE FILTER (Feb 2026): the "Staff who
      // can perform this" picker must ONLY surface *active* Care Team
      // practitioners. Management / Reservation / Store roles never
      // deliver services, so hiding them prevents mis-assignment and
      // keeps the "N selected" count truthful. Backend list endpoint
      // already returns care-team-only `staff_ids` per service; this
      // frontend gate is a defence-in-depth belt so a stale roster
      // response (e.g. cached response prior to a role change) can't
      // surface a stale non-Care-Team option in the dropdown.
      const CARE_TEAM_ROLE_ID = "role_preset_care_team";
      const careTeamOnly = (staff.data || []).filter(
        (s) => s.active !== false && s.role_id === CARE_TEAM_ROLE_ID,
      );
      setStaffOpts(careTeamOnly.map((s) => ({
        id: s.id,
        label: s.full_name,
        hint: s.position || "Care Team",
      })));
      setRoomOpts(rooms.data.filter((r) => r.active !== false).map((r) => ({ id: r.id, label: r.name })));
      setEquipmentOpts(eq.data.filter((e) => e.active !== false).map((e) => ({ id: e.id, label: e.name, hint: e.code || "" })));
      if (!selectedId && svcs.data.length) setSelectedId(svcs.data[0].service_id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [bid, selectedId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const s = services.find((x) => x.service_id === selectedId);
    if (s) {
      // Prune any legacy `staff_ids` that no longer resolve to an
      // active Care Team member — the picker only offers Care Team,
      // so the "N selected" count must mirror the same universe.
      const allowedIds = new Set(staffOpts.map((o) => o.id));
      setDraft({
        staff_ids: [...(s.staff_ids || [])].filter((id) => allowedIds.has(id)),
        room_ids: [...(s.room_ids || [])],
        equipment_ids: [...(s.equipment_ids || [])],
      });
    }
    else setDraft(null);
  }, [selectedId, services, staffOpts]);

  const filteredSvcs = services.filter((s) =>
    !search.trim() ? true : (s.service_name || "").toLowerCase().includes(search.trim().toLowerCase()),
  );

  const save = async () => {
    if (!selectedId || !draft) return;
    setSaving(true);
    try {
      // Send only the three mirrored dimensions — consumables are hidden.
      await staffApi.put(`/branches/${bid}/service-configs/${selectedId}`, {
        staff_ids: draft.staff_ids,
        room_ids: draft.room_ids,
        equipment_ids: draft.equipment_ids,
      });
      // Wait for BOTH the local Services-tab data AND the parent BranchSettings
      // employees / rooms state to finish reloading before releasing the
      // saving state and showing the success toast, so the operator can open
      // the Employee / Room wizard right afterwards without racing the fetch.
      await Promise.all([load(), Promise.resolve(onSaved && onSaved())]);
      toast.success("Service configuration saved. Employee & Room settings updated to match.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (services.length === 0) {
    return (
      <Card className="rounded-xl">
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          No services are available at this location yet. Enable them in the Brand catalog first
          (Brand Workspace → Services → tick this location under the service&apos;s Locations step).
        </CardContent>
      </Card>
    );
  }

  const selectedSvc = services.find((s) => s.service_id === selectedId);

  return (
    <Card className="rounded-xl">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-[480px]">
          {/* Service list */}
          <div className="border-b lg:border-b-0 lg:border-r border-border p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Services this location is enabled for. Configure who and what delivers each one.
            </p>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search services…"
                className="pl-8 h-9"
                data-testid="svc-list-search"
              />
            </div>
            <div className="max-h-[520px] overflow-auto rounded-md border border-border divide-y divide-border bg-white">
              {filteredSvcs.map((s) => (
                <button
                  key={s.service_id}
                  type="button"
                  onClick={() => setSelectedId(s.service_id)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-secondary ${selectedId === s.service_id ? "bg-primary/5 border-l-2 border-primary" : ""}`}
                  data-testid={`svc-item-${s.service_id}`}
                >
                  <p className="text-sm font-medium truncate">{s.service_name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {s.category || "Uncategorised"} · {s.duration_min}m
                    {s.has_override && <span className="ml-2 text-primary">• configured</span>}
                  </p>
                </button>
              ))}
              {filteredSvcs.length === 0 && <p className="p-4 text-xs text-muted-foreground text-center">No matches.</p>}
            </div>
          </div>

          {/* Editor */}
          <div className="p-6">
            {!draft || !selectedSvc ? (
              <p className="text-sm text-muted-foreground">Select a service on the left to configure delivery.</p>
            ) : (
              <div className="space-y-6" data-testid="svc-editor">
                <div>
                  <p className="tracking-overline text-[11px] text-muted-foreground">SERVICE</p>
                  <h3 className="font-editorial text-2xl mt-1">{selectedSvc.service_name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedSvc.category || "Uncategorised"} · {selectedSvc.duration_min} min
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <MultiSelectChips
                    label="Staff who can perform this"
                    options={staffOpts}
                    selected={draft.staff_ids}
                    onChange={(v) => setDraft({ ...draft, staff_ids: v })}
                    testid="staff"
                    placeholder="Search team members…"
                    disabled={readOnly}
                  />
                  <MultiSelectChips
                    label="Rooms this can be delivered in"
                    options={roomOpts}
                    selected={draft.room_ids}
                    onChange={(v) => setDraft({ ...draft, room_ids: v })}
                    testid="room"
                    placeholder="Search rooms…"
                    disabled={readOnly}
                  />
                  <MultiSelectChips
                    label="Equipment used"
                    options={equipmentOpts}
                    selected={draft.equipment_ids}
                    onChange={(v) => setDraft({ ...draft, equipment_ids: v })}
                    testid="equipment"
                    placeholder="Search equipment…"
                    empty="Add equipment on the Equipment tab first."
                    disabled={readOnly}
                  />
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Staff and Room selections here are mirrored to the Employee wizard and Room settings —
                  changes made in either place stay in sync automatically.
                </p>

                {perms.edit && (
                  <div className="flex justify-end">
                    <Button onClick={save} disabled={saving} data-testid="save-service-config-btn">
                      {saving ? "Saving…" : "Save configuration"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

